'use client'

import { useTheme } from '@/contexts/ThemeContext'

export default function PrivacyPage() {
  const { theme } = useTheme()

  return (
    <div className={`min-h-screen py-12 px-4 ${
      theme === 'light'
        ? 'bg-gradient-to-br from-white via-blue-50 to-cyan-50'
        : 'bg-gray-900'
    }`}>
      <div className="max-w-4xl mx-auto">
        <h1 className={`text-3xl xxs:text-xl font-bold mb-8 xxs:mb-4 ${
          theme === 'light' ? 'text-gray-800' : 'text-white'
        }`}>개인정보처리방침</h1>

        <div className={`rounded-lg p-8 xxs:p-4 space-y-8 xxs:space-y-4 ${
          theme === 'light'
            ? 'bg-white shadow-lg text-gray-700'
            : 'bg-gray-800 text-gray-300'
        }`}>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>1. 개인정보의 수집 및 이용목적</h2>
            <p className="mb-3">오늘다이빙은 다음과 같은 목적으로 개인정보를 수집 및 이용합니다:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>회원 관리:</strong> 본인확인, 가입의사 확인, 중복가입 방지</li>
              <li><strong>서비스 제공:</strong> 프리다이빙 버디 매칭, 안전 관리, 예약 관리</li>
              <li><strong>서비스 개선:</strong> 이용 통계 분석, 신규 서비스 개발</li>
              <li><strong>안전 관리:</strong> 부정 이용 방지, 분쟁 조정을 위한 기록 보존</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>2. 수집하는 개인정보 항목</h2>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold mb-2">✅ 필수 수집 항목</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>카카오 계정 정보 (ID, 이메일, 닉네임)</li>
                  <li>프로필 이미지 (카카오 프로필)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">⚪ 선택 수집 항목</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>휴대전화번호 (본인인증, 매칭 알림)</li>
                  <li>자격증 정보 (레벨별 매칭)</li>
                  <li>활동 지역 (지역별 매칭)</li>
                  <li>자격증 이미지 (인증 목적)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>3. 개인정보의 보유 및 이용기간</h2>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold mb-2">기본 원칙</h4>
                <p>수집·이용 목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">보유 기간</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>회원정보:</strong> 회원 탈퇴 시까지</li>
                  <li><strong>매칭 기록:</strong> 서비스 종료 후 1년</li>
                  <li><strong>결제 정보:</strong> 법정 보존기간 5년</li>
                  <li><strong>분쟁 관련:</strong> 분쟁 해결 시까지</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>4. 개인정보의 안전성 확보조치</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>암호화:</strong> 개인정보는 암호화하여 저장 및 전송</li>
              <li><strong>접근 제한:</strong> 개인정보 접근 권한을 최소한의 인원으로 제한</li>
              <li><strong>보안 프로그램:</strong> 해킹 등에 대비한 기술적 대책 수립</li>
              <li><strong>접속 기록:</strong> 개인정보 처리시스템 접속 기록 보관</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>5. 이용자 권리 및 행사 방법</h2>
            <div className="space-y-3">
              <p>이용자는 언제든지 다음 권리를 행사할 수 있습니다:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>열람·정정·삭제:</strong> 앱 내 프로필 설정에서 직접 수정</li>
                <li><strong>처리정지:</strong> 서비스 이용 중단 요청</li>
                <li><strong>회원탈퇴:</strong> 설정 &gt; 계정 삭제</li>
              </ul>
              <div className={`mt-4 p-3 rounded ${
                theme === 'light' ? 'bg-yellow-50 border border-yellow-200' : 'bg-yellow-900/20 border border-yellow-700'
              }`}>
                <p className={`text-sm ${theme === 'light' ? 'text-yellow-800' : 'text-yellow-300'}`}>
                  ⚠️ 탈퇴 시 모든 개인정보가 즉시 삭제되며, 복구가 불가능합니다.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>6. 개인정보 보호책임자</h2>
            <div className={`p-4 rounded-lg ${
              theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-gray-700 border border-gray-600'
            }`}>
              <div className="space-y-2">
                <p><strong>직책:</strong> 서비스 총괄팀장</p>
                <p><strong>이메일:</strong> 0wonchorok@naver.com</p>
                <p><strong>처리시간:</strong> 평일 09:00-18:00 (영업일 기준 3일 이내 답변)</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-xl xxs:text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>7. 고지의 의무</h2>
            <p>현 개인정보처리방침의 내용 추가, 삭제 및 수정이 있을 시에는 개정 최소 7일 전부터 앱 내 공지사항을 통해 고지할 것입니다.</p>
          </section>

        </div>
      </div>
    </div>
  )
}